/**
 * LotoStream Pro ADMIN — Shell principal del SPA (ES Module).
 *
 * Centraliza solo el chasis de la aplicación. La lógica de negocio vive
 * en los módulos de `modules/` que se importan abajo.
 *
 * Mapa de secciones (orden del archivo):
 *   1. API BASE              env vars Vite (.env.production / .development).
 *   2. CONFIG GLOBAL         CONFIG, tokens, constantes UI.
 *   3. API CLIENT            re-export de AdminApiClient (services/).
 *   4. APP SHELL             objeto App con estado global, routing,
 *                            navegación, modales, toast, utilidades.
 *
 * Módulos de negocio importados (delegados, no contienen lógica aquí):
 *   - games/lotowheel:        sorteos lotéricos, simulator, modales.
 *   - platform-nodos:         árbol fractal de nodos.
 *   - platform-dispositivos:  POS/screens/cajas.
 *   - platform-administradores: usuarios admin.
 *   - finanzas, marketing, clientes, tickets, actividad, sistema, pozos.
 *
 * La regla de oro: este archivo NO debe crecer con lógica de negocio.
 * Toda nueva feature va en su propio módulo bajo `modules/`.
 */

import { MonedaService } from './services/moneda-service.js';
import { conMonedaDeVista } from './services/moneda-vista-en-requests.js';
import { DesgloseMonedaPopover } from './components/desglose-moneda-popover.js';
import {
    IDIOMAS_ADMIN,
    getIdiomaAdmin,
    siguienteIdiomaAdmin,
    traducirElementosEstaticosDelDom,
    t,
} from './services/i18n-admin.js';
import { AdminApiClient } from './services/admin-api-client.js';
import { icon } from './utils/icon.js';
import { escapeHtmlParaInyeccionSegura } from './utils/escape-html-para-inyeccion-segura.js';
import { FuzzySearch } from './fuzzy-search.js';
import { ROL_ADMIN } from '@shared/config/business-constants.js';
import { ENDPOINTS } from '@shared/config/api-nomenclatura.js';
import { crearConfiguracionDeRuedaParaTipo } from '@shared/config/games/lotowheel/rueda-config-factory.js';
import { TimelineBar } from './components/timeline-bar.js';
import { LotowheelModule } from './modules/games/lotowheel/lotowheel.js';
import { PlatformModule } from './modules/platform.js';
import { getModulo, getModuloSiYaCargado, getModuloConInit, proxyLazyFuncion } from './services/lazy-modules.js';
import { PresenciaLive } from './modules/presencia-live.js';
const cargarSaldosDeRedOperativa = proxyLazyFuncion('platformSaldosRed', 'cargarSaldosDeRedOperativa');
import { key as storageKey } from '@shared/config/storage-namespace.js';

// ═══════════════════════════════════════════════════════════════════════
// Bolita del numero ganador (command center): pinta agnostica por paleta del
// tema activo via [data-paleta="paleta-N"] en el CSS (10-command-center.css).
// Si el tipo de ruleta del sorteo no se puede resolver, paleta = '' → el CSS
// aplica un fallback neutro. Cero hardcoded de hex.
// ═══════════════════════════════════════════════════════════════════════
const PALETA_VACIA = '';

// ═══════════════════════════════════════════════════════════════════════
// API BASE — Vite inyecta desde el .env del entorno (ver infra/brands/<id>.json).
// Prod/staging: URL absoluta del adminApi (ej. https://admin-api.<zona>) → fetch reescribe /api/...
// Dev:          '' → proxy de Vite maneja /api → backend local.
// Sin fallback hardcoded: build sin VITE_API_BASE en prod debe romper ruidoso.
const VITE_API_BASE = import.meta.env.VITE_API_BASE;
if (VITE_API_BASE === undefined) {
    throw new Error('[Admin] VITE_API_BASE no está definido. Verificar .env del build.');
}
if (!import.meta.env.DEV && VITE_API_BASE === '') {
    throw new Error('[Admin] VITE_API_BASE vacío en build no-dev. Verificar .env.<entorno>.');
}
const API_BASE = VITE_API_BASE;

// ═══════════════════════════════════════════════════════════════════════
// CONFIG GLOBAL — token storage key, intervalos, constantes UI compartidas.
// ═══════════════════════════════════════════════════════════════════════
// SSOT: debe coincidir con admin-api-client.js (mismo storageKey).
const ADMIN_TOKEN_KEY = storageKey('admin_token');

// Módulo lazy que cada view:tab necesita (ver loadTabData/loadView) — key debe
// existir en services/lazy-modules.js. conInit:true = el módulo antes corría
// .init() en el bootstrap (showApp()); ahora corre una sola vez, la primera
// vez que se resuelve, vía getModuloConInit.
const MODULO_POR_TAB = {
    'plataforma:red': { key: 'platformNodos' },
    'plataforma:dispositivos': { key: 'platformDispositivos' },
    'clientes:jugadores': { key: 'clientes', conInit: true },
    'finanzas:pasarelas': { key: 'finanzas', conInit: true },
    'finanzas:transferencias': { key: 'finanzas', conInit: true },
    'finanzas:b2b': { key: 'finanzasB2b' },
    'actividad:wallet': { key: 'finanzas', conInit: true },
    'finanzas:cierres': { key: 'finanzas', conInit: true },
    'finanzas:alertas': { key: 'finanzas', conInit: true },
    'marketing:promociones': { key: 'marketing', conInit: true },
    'marketing:analisis': { key: 'marketing', conInit: true },
    'marketing:presets': { key: 'marketing', conInit: true },
    'marketing:referidos': { key: 'marketing', conInit: true },
    'sistema:auditoria': { key: 'sistema', conInit: true },
    'sistema:sesiones': { key: 'sistema', conInit: true },
    'sistema:series-tickets': { key: 'sistema', conInit: true },
    'sistema:fleet-crashes': { key: 'sistema', conInit: true },
    'sistema:fleet-whatsapp': { key: 'sistema', conInit: true },
    'plataforma:entidades': { key: 'platformAdministradores' },
    'juegos:tickets': { key: 'tickets', conInit: true },
    'actividad:tickets': { key: 'tickets', conInit: true },
    'juegos:pozos-comunes': { key: 'pozos' },
    'actividad:integradores': { key: 'actividad', conInit: true },
    'marketing:cupones': { key: 'marketingCupones' },
    'marketing:tickets': { key: 'marketingTickets' },
};

const CONFIG = {
    apiUrl: '/api',
    pollInterval: 3000,
    tokenKey: ADMIN_TOKEN_KEY,
};

// Interceptar fetch para reescribir URLs relativas /api/... → URL absoluta
// y recargar si la sesión expiró (401)
if (API_BASE) {
    const _originalFetch = window.fetch;
    window.fetch = function (input, init) {
        if (typeof input === 'string' && input.startsWith('/api/')) {
            input = API_BASE + input;
        }
        return _originalFetch.call(this, input, init).then(response => {
            if (response.status === 401 && window.App?.currentUser) {
                localStorage.removeItem(ADMIN_TOKEN_KEY);
                window.location.reload();
            }
            return response;
        });
    };
}

// ═══════════════════════════════════════════════════════════════════════
// API CLIENT — re-export de AdminApiClient (lógica en services/).
// Los 14+ módulos que importan { Api } siguen funcionando sin cambios.
// AdminApiClient.init() se llama al final del archivo (necesita App ya definido).
// ═══════════════════════════════════════════════════════════════════════
export { AdminApiClient as Api };

// ═══════════════════════════════════════════════════════════════════════
// APP SHELL — estado global, routing, navegación, modales, toast, utils.
// La regla de oro: NO agregar lógica de negocio aquí. Cada feature en su módulo.
// ═══════════════════════════════════════════════════════════════════════
export const App = {
    currentView: 'command-center',
    currentUser: null,
    currentSorteos: [],
    filteredSorteos: [],
    expandedSorteoId: null,
    sorteoFilters: { conApuestas: false, casaGana: false, casaPierde: false },
    sorteoSearcher: null,
    sorteoRange: '20',
    sorteoConfigFilter: 'all',
    sorteoConfigs: [],

    // ---- INIT ----
    init() {
        // Selector de idioma del panel (sidebar): opciones desde el SSOT de
        // i18n-admin; el change lo maneja event-bindings (app:cambiarIdiomaAdmin).
        const botonIdioma = document.getElementById('adminIdiomaSelector');
        if (botonIdioma) {
            const actual = IDIOMAS_ADMIN.find(i => i.codigo === getIdiomaAdmin()) || IDIOMAS_ADMIN[0];
            const proximo = siguienteIdiomaAdmin();
            // Bandera + NOMBRE: una bandera sola no dice el idioma (🇧🇷 podría
            // leerse como "Brasil" y no como "Português") y dejaba el botón medio
            // vacío al lado del selector de moneda, que sí es ancho completo.
            botonIdioma.innerHTML =
                `<span class="idioma-bandera">${actual.bandera}</span>` +
                `<span class="idioma-nombre">${escapeHtmlParaInyeccionSegura(actual.etiqueta)}</span>`;
            botonIdioma.title = `${actual.etiqueta} — clic: ${proximo.etiqueta}`;
        }

        // El selector de moneda NO se pinta acá: su opción "Nativa" nombra la moneda
        // del usuario, que recién se conoce tras cargar /api/admin/me. Se pinta desde
        // pintarSelectorDeMonedaDeVista(), llamado después de setContexto().

        // Menú lateral y demás estáticos del index.html marcados con [data-i18n]
        traducirElementosEstaticosDelDom();

        // Verificar si hay sesión activa
        this.checkAuth();
    },

    // ---- AUTENTICACIÓN ----
    // Obtiene el token de sessionStorage o localStorage
    getToken() {
        return sessionStorage.getItem(CONFIG.tokenKey) || localStorage.getItem(CONFIG.tokenKey);
    },

    // Guarda el token según la opción "Recordarme"
    setToken(token, remember) {
        if (remember) {
            localStorage.setItem(CONFIG.tokenKey, token);
            sessionStorage.removeItem(CONFIG.tokenKey);
        } else {
            sessionStorage.setItem(CONFIG.tokenKey, token);
            localStorage.removeItem(CONFIG.tokenKey);
        }
    },

    // Elimina el token de ambos storages
    clearToken() {
        localStorage.removeItem(CONFIG.tokenKey);
        sessionStorage.removeItem(CONFIG.tokenKey);
    },

    async checkAuth() {
        const token = this.getToken();
        if (!token) {
            this.showLogin();
            return;
        }

        try {
            const res = await this.fetchWithAuth('/api/admin/me');
            if (res.ok && res.usuario) {
                this.currentUser = res.usuario;
                // Offset UTC del brand activo (Brasil -3, Perú -5, etc) — SSOT para convertir
                // fechas locales del selector de rango a UTC antes de mandarlas al backend.
                // Fallback 3 (São Paulo) si el backend no lo manda (versión vieja de API).
                this.offsetHorasUtc = typeof res.offsetHorasUtc === 'number' ? res.offsetHorasUtc : 3;
                this.showApp();
            } else {
                this.clearToken();
                this.showLogin();
            }
        } catch (e) {
            console.error('Auth check failed:', e);
            this.clearToken();
            this.showLogin();
        }
    },

    showLogin() {
        document.getElementById('loginScreen').style.display = 'flex';
        document.getElementById('appContainer').style.display = 'none';
        // Iconos ya son <img> locales, no requiere inicializacion
        // Sin sesión no se pollea: corta el interval de loadAlertas/loadCommandCenter.
        if (this._pollIntervalId) {
            clearInterval(this._pollIntervalId);
            this._pollIntervalId = null;
        }
    },

    showApp() {
        document.getElementById('loginScreen').style.display = 'none';
        document.getElementById('appContainer').style.display = 'flex';

        // Mostrar info del usuario
        const nodoNombreUsuario = document.getElementById('userName');
        nodoNombreUsuario.textContent = this.currentUser.nombre;
        // El nombre se recorta con ellipsis para no ensanchar el sidebar: el title
        // deja leerlo completo al pasar el mouse.
        nodoNombreUsuario.title = this.currentUser.nombre || '';
        document.getElementById('userRole').textContent = this.getRolLabel(this.currentUser.rol);

        // Moneda del nodo del usuario (herencia fractal desde /api/admin/me)
        if (this.currentUser.moneda) {
            MonedaService.setContexto(this.currentUser.moneda);
        }
        // Recién ahora se conoce la moneda nativa: el selector puede nombrarla.
        this.pintarSelectorDeMonedaDeVista();

        // Configurar navegación según rol y capabilities
        this.setupRoleBasedNavigation();

        // Dynamic Game Loading: Cargar tipos de juego y renderizar tabs
        this.loadGameTypes();

        // Inicializar modulos. Los módulos pesados (Finanzas/PlatformNodos/
        // PlatformDispositivos/Clientes/Marketing/Sistema) NO se llaman acá —
        // lazy-load recién al entrar a su tab (ver loadTabData), vía
        // services/lazy-modules.js. Sus init()/load() ya son idempotentes con
        // guard de contenedor null (el HTML del custom element existe desde el
        // bootstrap, solo oculto), así que llamarlos diferido es equivalente a
        // llamarlos acá.
        LotowheelModule.init();
        PlatformModule.init();
        // Presencia en vivo: LED de dispositivos del árbol se actualiza por SSE
        // (canal público admin:presence). Degradación silenciosa si no hay SSE_URL.
        PresenciaLive.init();
        // Cargar tasas de cambio en background para activar conversiones multimoneda
        this.fetchWithAuth('/api/admin/tasas-cambio')
            .then(data => {
                if (data?.tasas) MonedaService.setTasas(data.tasas);
            })
            .catch(() => {}); // Silencioso — si falla, las conversiones usan 1x como fallback
        // Inicializar app
        this.setupNavigation();
        this.setupModalTabs();
        this.navigate('command-center');
        this.loadAlertas();
        // Alertas refrescan siempre — el badge global del nav lo lee sin
        // importar la vista activa. CommandCenter solo cuando se ve.
        // Flags in-flight: un tick no lanza una carga si la anterior sigue pendiente
        // (los loaders tienen timeout, así que siempre settlean y liberan el flag).
        let alertasEnVuelo = false;
        let commandCenterEnVuelo = false;
        // showApp() corre en cada login: clear defensivo para que nunca haya más de
        // un interval vivo, sin depender de que showLogin() haya corrido antes.
        if (this._pollIntervalId) clearInterval(this._pollIntervalId);
        this._pollIntervalId = setInterval(() => {
            if (document.hidden) return; // tab en segundo plano — no pollear
            if (!alertasEnVuelo) {
                alertasEnVuelo = true;
                this.loadAlertas().finally(() => {
                    alertasEnVuelo = false;
                });
            }
            if (this.currentView === 'command-center' && !commandCenterEnVuelo) {
                commandCenterEnVuelo = true;
                // Defensivo: la carga de datos nunca rechaza (callAdmin devuelve null),
                // pero un throw del render post-await sí — log y el próximo tick reintenta.
                this.loadCommandCenter()
                    .catch(err => console.warn('[poll] command-center falló:', err?.message || err))
                    .finally(() => {
                        commandCenterEnVuelo = false;
                    });
            }
        }, CONFIG.pollInterval);
        // Iconos ya son <img> locales, no requiere inicializacion
    },

    /**
     * Dynamic Game Loading - Carga tipos de juego desde API y renderiza tabs
     * Evita hardcodear tabs de juegos en el HTML. Cuando se agregue un nuevo
     * juego (ej: Bingo), solo hay que agregarlo a game_types en la DB.
     */
    async loadGameTypes() {
        const container = document.getElementById('gameTypeTabs');
        if (!container) return;

        try {
            const data = await AdminApiClient.getGameTypes();
            if (!data || !data.gameTypes) {
                container.innerHTML = `<span class="tab-error">${t('Error cargando juegos')}</span>`;
                return;
            }

            // Mapeo de iconos por code (fallback si no viene de DB)
            const iconMap = {
                lotowheel: 'disc',
                bingo: 'layers',
                keno: 'list',
                slots: 'gem',
            };

            // Renderizar tabs de game_types
            let html = data.gameTypes
                .map((gt, idx) => {
                    const iconName = gt.icon || iconMap[gt.code] || 'disc';
                    const isActive = idx === 0 ? 'active' : '';
                    return `
                    <button class="tab-btn ${isActive}" data-tab="${gt.code}" data-action="app:switchGameTab">
                        ${icon(iconName)} ${gt.nombre}
                    </button>
                `;
                })
                .join('');

            // Agregar tab estático "Pozos" al final (suprajuego)
            html += `
                <button class="tab-btn" data-tab="pozos-comunes" data-action="app:switchGameTab">
                    ${icon('wallet')} Pozos
                </button>`;

            container.innerHTML = html;

            // Guardar game_types para uso posterior
            this.gameTypes = data.gameTypes;
            this.editoresInstant = [];
            try {
                this.editoresInstant = await Api.callAdmin('/admin/juegos/instant', 'GET');
            } catch (error) {
                console.warn('[Editores INSTANT] No disponibles:', error);
            }
            for (const editor of this.editoresInstant) {
                const id = `tab-juegos-${editor.gameCode}`;
                if (document.getElementById(id)) continue;
                const panel = document.createElement('div');
                panel.id = id;
                panel.className = 'tab-content';
                panel.style.display = 'none';
                document.getElementById('view-juegos').append(panel);
            }
        } catch (error) {
            console.error('[Dynamic Game Loading] Error:', error);
            container.innerHTML = `<span class="tab-error">${t('Error cargando juegos')}</span>`;
        }
    },

    async loadAlertas() {
        const container = document.getElementById('alertasContainer');
        const list = document.getElementById('alertasList');
        const count = document.getElementById('alertasCount');
        const navBadge = document.getElementById('navAlertasBadge');

        // Timeout: un fetch que nunca settlea dejaría clavado el guard in-flight del
        // polling (alertasEnVuelo) y el badge moriría en silencio.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.FETCH_TIMEOUT_MS);
        try {
            const response = await fetch('/api/admin/alertas-operativas', {
                headers: { 'x-admin-token': this.getToken() },
                signal: controller.signal,
            });
            const data = await response.json();

            // Conteo de críticas — el badge global solo cuenta críticas, no
            // alertas medias/altas. Una alerta crítica es algo que requiere
            // atención inmediata (sorteo atascado, dispositivo offline >10min,
            // settlement bloqueado). Las medias/altas viven en el panel
            // operativo ampliado.
            const alertasCriticas = (data.alertas || []).filter(a => a.impacto === 'critico');
            const totalCriticas = alertasCriticas.length;

            // Badge global del nav — solo cuenta críticas, visible desde cualquier vista.
            if (navBadge) {
                navBadge.textContent = String(totalCriticas);
                navBadge.style.display = totalCriticas > 0 ? 'inline-flex' : 'none';
            }

            if (!container || !list) return;

            if (!data.alertas || data.alertas.length === 0) {
                container.style.display = 'none';
                return;
            }

            container.style.display = 'block';
            count.textContent = data.total;

            // El panel entra SIEMPRE colapsado (decisión dueño 2026-07-18): el badge
            // con el conteo ya avisa; expandir es decisión del operador, ni con críticas.

            // Iconos por tipo de alerta operativa
            const ALERT_ICONS = {
                tv_offline: 'monitor-off',
                corte_atrasado: 'scissors',
                sorteo_atascado: 'alert-triangle',
            };

            // Clases por impacto
            const IMPACT_CLASSES = {
                critico: 'badge-danger',
                alto: 'badge-warning',
                medio: 'badge-secondary',
            };

            list.innerHTML = data.alertas
                .map(alerta => {
                    const nombreIcono = ALERT_ICONS[alerta.tipo] || 'alert-triangle';
                    const impactClass = IMPACT_CLASSES[alerta.impacto] || 'badge-warning';
                    return `
                    <div class="alert-item ${impactClass}" style="padding:8px 12px; border-radius:8px; display:flex; align-items:center; gap:8px; font-size:13px; cursor:pointer; background:var(--bg-tertiary); border:1px solid var(--border-glass);"
                         data-action="app:handleAlertaClick" data-target-tipo="${alerta.target_tipo}" data-target-id="${alerta.target_id}" data-tipo="${alerta.tipo}">
                        ${icon(nombreIcono, '', { style: 'opacity:0.8;' })}
                        <span>${escapeHtmlParaInyeccionSegura(alerta.mensaje)}</span>
                    </div>
                `;
                })
                .join('');
        } catch (error) {
            console.error('Error cargando alertas:', error);
        } finally {
            clearTimeout(timeoutId);
        }
    },

    /**
     * Selector global de moneda de VISUALIZACIÓN (mismo patrón que el idioma:
     * persiste en localStorage y recarga para repintar todos los reportes).
     * Se pinta DESPUÉS de MonedaService.setContexto(): la opción por defecto
     * nombra la moneda nativa del usuario, que llega con /api/admin/me. Pintarlo
     * antes mostraba la moneda por defecto del sistema (PEN) a un admin de Brasil.
     */
    pintarSelectorDeMonedaDeVista() {
        const selector = document.getElementById('adminMonedaVistaSelector');
        if (!selector) return;
        const vistaElegida = MonedaService.getMonedaVista();
        const isoNativa = MonedaService.getContexto();
        // "Nativa" a secas no dice nada: se nombra la moneda que se ve realmente.
        const etiquetaNativa = isoNativa
            ? `${t('Nativa')} · ${isoNativa} (${MonedaService.getSimboloDe(isoNativa)})`
            : t('Nativa');
        selector.innerHTML =
            `<option value="">${etiquetaNativa}</option>` +
            MonedaService.ORDER.map(iso => {
                // Símbolo + nombre + ISO: cinco monedas comparten el símbolo "$"
                // (COP/ARS/CLP/MXN/USD), así que el símbolo solo no distingue cuál
                // se está eligiendo. Mismo criterio que el selector de liquidación.
                const preset = MonedaService.getPreset(iso);
                const etiqueta = `${preset.simbolo} — ${t(preset.nombre)} (${iso})`;
                return `<option value="${iso}" ${vistaElegida === iso ? 'selected' : ''}>${etiqueta}</option>`;
            }).join('');
        // El selector convierte los reportes CONSOLIDADOS (los que el backend agrega
        // por moneda); los importes de una entidad puntual —el saldo de un jugador,
        // el monto de un movimiento— se siguen mostrando en su moneda propia, con su
        // símbolo. Decirlo en el title evita la lectura de que TODO el panel cambia
        // de unidad (SPEC_admin-moneda-de-vista.md §5 Fase C, invariante 16).
        selector.title = vistaElegida
            ? t(
                  'Los reportes consolidados se convierten a esta moneda (≈ aproximado). Los importes de un solo nodo o movimiento conservan su moneda propia.'
              )
            : t('Cada importe se muestra en su moneda propia. Elija una para consolidar los reportes a esa moneda.');
        if (this._selectorMonedaVistaConectado) return;
        this._selectorMonedaVistaConectado = true;
        selector.addEventListener('change', () => {
            MonedaService.setMonedaVista(selector.value || null);
            location.reload();
        });
    },

    handleAlertaClick(entidadTipo, entidadId, alertaTipo) {
        if (entidadTipo === 'dispositivo') {
            this.navigate('plataforma');
            // Navegar a tab de Dispositivos (atributo correcto: data-tab)
            setTimeout(() => {
                document.querySelector('[data-tab="dispositivos"]')?.click();
            }, 100);
        } else if (entidadTipo === 'nodo' || entidadTipo === 'punto_venta') {
            if (alertaTipo === 'corte_atrasado') {
                this.navigate('finanzas');
                // Navegar a tab de cortes
                setTimeout(() => {
                    document.querySelector('[data-tab="cortes"]')?.click();
                }, 100);
            }
        }
    },

    // Estado de alertas colapsadas (cerradas por defecto al entrar)
    alertasCollapsed: true,

    toggleAlertas() {
        const list = document.getElementById('alertasList');
        const chevron = document.getElementById('alertasChevron');
        if (!list || !chevron) return;

        this.alertasCollapsed = !this.alertasCollapsed;
        list.style.display = this.alertasCollapsed ? 'none' : 'flex';
        chevron.style.transform = this.alertasCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)';
    },

    async openAlertasConfig() {
        try {
            const response = await fetch('/api/admin/alertas-operativas/config', {
                headers: { 'x-admin-token': this.getToken() },
            });
            const config = await response.json();

            const content = `
                <div style="display: flex; flex-direction: column; gap: 16px;">
                    <p style="color: var(--text-secondary); font-size: 13px; margin: 0;">
                        Configura los umbrales para las alertas operativas. Los cambios se aplican inmediatamente.
                    </p>

                    <div class="form-group">
                        <label class="form-label">TV Offline (minutos sin señal)</label>
                        <input type="number" id="alertConfigTvOffline" class="form-input"
                               value="${config.TV_OFFLINE_THRESHOLD_MIN || 10}" min="1" max="60">
                        <small style="color: var(--text-muted);">Alerta cuando una TV no envía pulsos en este tiempo</small>
                    </div>

                    <div class="form-group">
                        <label class="form-label">Corte Atrasado (días sin corte)</label>
                        <input type="number" id="alertConfigCorteAtrasado" class="form-input"
                               value="${config.CORTE_ATRASADO_DIAS || 3}" min="1" max="30">
                        <small style="color: var(--text-muted);">Alerta cuando un PV tiene producción sin corte</small>
                    </div>

                    <div class="form-group">
                        <label class="form-label">Intervalo de verificación (segundos)</label>
                        <input type="number" id="alertConfigInterval" class="form-input"
                               value="${Math.floor((config.CHECK_INTERVAL_MS || 60000) / 1000)}" min="30" max="300">
                        <small style="color: var(--text-muted);">Cada cuánto se verifican las alertas</small>
                    </div>

                    <div style="display: flex; gap: 8px; justify-content: flex-end; margin-top: 8px;">
                        <button class="btn btn-secondary" data-action="app:closeModalGeneric">Cancelar</button>
                        <button class="btn btn-primary" data-action="app:saveAlertasConfig">${t('Guardar')}</button>
                    </div>
                </div>
            `;

            this.showGenericModal(t('Configuración de Alertas'), content, { hideFooter: true });
        } catch (error) {
            console.error('Error cargando config de alertas:', error);
            this.toast(t('Error cargando configuración'), 'error');
        }
    },

    async saveAlertasConfig() {
        const tvOffline = parseInt(document.getElementById('alertConfigTvOffline').value);
        const corteAtrasado = parseInt(document.getElementById('alertConfigCorteAtrasado').value);
        const interval = parseInt(document.getElementById('alertConfigInterval').value);

        if (isNaN(tvOffline) || isNaN(corteAtrasado) || isNaN(interval)) {
            this.toast(t('Valores inválidos'), 'error');
            return;
        }

        try {
            const response = await fetch('/api/admin/alertas-operativas/config', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-admin-token': this.getToken(),
                },
                body: JSON.stringify({
                    TV_OFFLINE_THRESHOLD_MIN: tvOffline,
                    CORTE_ATRASADO_DIAS: corteAtrasado,
                    CHECK_INTERVAL_MS: interval * 1000,
                }),
            });

            if (response.ok) {
                this.closeModal('modalGeneric');
                this.toast(t('Configuración guardada'), 'success');
                // Forzar re-check de alertas operativas
                await fetch('/api/admin/alertas-operativas/check', {
                    method: 'POST',
                    headers: { 'x-admin-token': this.getToken() },
                });
                this.loadAlertas();
            } else {
                throw new Error('Error guardando');
            }
        } catch (error) {
            console.error('Error guardando config:', error);
            this.toast(t('Error guardando configuración'), 'error');
        }
    },

    getRolLabel(rol) {
        const labels = {
            superadmin: 'Super Admin',
            operador: 'Operador',
            representante: 'Representante',
            punto_venta: 'Punto de Venta',
            caja: 'Caja',
        };
        return labels[rol] || rol;
    },

    /**
     * Verifica si el usuario actual tiene una capability específica
     * @param {string} capability - Nombre de la capability (ej: 'pv.create')
     * @returns {boolean}
     */
    can(capability) {
        if (!this.currentUser) return false;

        // Superadmin tiene todo
        if (this.currentUser.rol === ROL_ADMIN.SUPERADMIN) return true;

        // Verificar capabilities del usuario
        const caps = this.currentUser.capabilities || {};

        // Wildcard check
        if (caps['*'] === true) return true;

        return caps[capability] === true;
    },

    /**
     * Verifica si el usuario es superadmin
     * @returns {boolean}
     */
    isSuperadmin() {
        return this.currentUser?.rol === ROL_ADMIN.SUPERADMIN;
    },

    /**
     * Obtiene el filtro de contexto del usuario (para Row-Level Security)
     * @returns {Object|null}
     */
    getContextFilter() {
        return this.currentUser?.contextFilter || null;
    },

    /**
     * Configura la navegación según el rol del usuario
     * Oculta/muestra secciones y tabs basado en capabilities
     */
    setupRoleBasedNavigation() {
        // Sidebar dinámico: ocultar nav-items según capabilities
        let firstVisible = null;
        document.querySelectorAll('.nav-item[data-cap]').forEach(item => {
            const cap = item.dataset.cap;
            const allowed = this.can(cap);
            item.style.display = allowed ? '' : 'none';
            if (allowed && !firstVisible) firstVisible = item.dataset.view;
        });

        // Si la vista activa fue ocultada, navegar a la primera visible
        if (firstVisible && !this.can('view.command_center')) {
            this.navigate(firstVisible);
        }

        // Botones de crear: ocultar según capabilities
        this._setupCreateButtons();
    },

    /**
     * Configura botones de crear según capabilities
     */
    _setupCreateButtons() {
        const btnOperador = document.getElementById('btnNuevoOperador');
        if (btnOperador) btnOperador.style.display = this.can('nodo.create') ? '' : 'none';

        const btnRep = document.getElementById('btnNuevoRepresentante');
        if (btnRep) btnRep.style.display = this.can('nodo.create') ? '' : 'none';

        const btnPV = document.getElementById('btnNuevoPuntoVenta');
        if (btnPV) btnPV.style.display = this.can('nodo.create') ? '' : 'none';

        const btnCaja = document.getElementById('btnNuevaCaja');
        if (btnCaja) btnCaja.style.display = this.can('nodo.create') ? '' : 'none';

        const btnUsuario = document.getElementById('btnNuevoUsuario');
        if (btnUsuario) btnUsuario.style.display = this.can('admin.create') ? '' : 'none';
    },

    async handleLogin(event) {
        event.preventDefault();

        const email = document.getElementById('loginEmail').value;
        const password = document.getElementById('loginPassword').value;
        const rememberMe = document.getElementById('rememberMe')?.checked || false;
        const btn = document.getElementById('loginBtn');
        const errorDiv = document.getElementById('loginError');

        btn.disabled = true;
        btn.textContent = t('Ingresando...');
        errorDiv.style.display = 'none';

        try {
            const res = await fetch('/api/admin/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });

            const data = await res.json();

            if (res.ok && data.token) {
                this.setToken(data.token, rememberMe);
                this.currentUser = data.usuario;
                this.showApp();
            } else if (data.code === 'cuenta_sin_contrasena') {
                // Onboarding universal: la cuenta existe pero nació sin contraseña
                // (o fue reseteada) → derivar al flujo de activación por código.
                this.mostrarRecuperacionCuenta(email, true);
            } else {
                errorDiv.textContent = data.error || t('Error al iniciar sesión');
                errorDiv.style.display = 'block';
            }
        } catch (e) {
            console.error('Login error:', e);
            errorDiv.textContent = t('Error de conexión');
            errorDiv.style.display = 'block';
        } finally {
            btn.disabled = false;
            btn.textContent = t('Ingresar');
        }

        return false;
    },

    /**
     * Flujo "activá tu cuenta" / "olvidé mi contraseña" del panel admin
     * (onboarding universal): código de 6 dígitos al correo REGISTRADO de la
     * cuenta → contraseña nueva (doble). El backend decide activación vs
     * recuperación según el estado real; este panel es el mismo para ambos.
     * Pre-login: fetch crudo, sin token. Anti-enumeración: el backend responde
     * uniforme al solicitar — acá solo informamos "si existe, te llegó".
     */
    mostrarRecuperacionCuenta(emailPrefill = '', esActivacion = false) {
        const form = document.getElementById('loginForm');
        const panel = document.getElementById('recuperacionPanel');
        if (!form || !panel) return;
        form.style.display = 'none';
        panel.style.display = 'block';

        const titulo = esActivacion ? t('Activá tu cuenta') : t('Recuperar contraseña');
        const intro = esActivacion
            ? t('Tu cuenta aún no tiene contraseña. Te enviamos un código a tu correo para que crees la tuya.')
            : t('Te enviamos un código al correo de tu cuenta para crear una contraseña nueva.');
        panel.innerHTML = `
            <div style="margin-bottom:12px">
                <div style="font-weight:600; margin-bottom:4px">${titulo}</div>
                <div style="font-size:13px; color:var(--text-secondary)">${intro}</div>
            </div>
            <div class="form-group">
                <label class="form-label">Email</label>
                <input type="email" class="form-input" id="recEmail" value="${emailPrefill.replace(/"/g, '&quot;')}" autocomplete="username">
            </div>
            <div id="recPaso2" style="display:none">
                <div class="form-group">
                    <label class="form-label">${t('Código de 6 dígitos')}</label>
                    <input type="text" class="form-input" id="recCodigo" inputmode="numeric" maxlength="6" autocomplete="one-time-code">
                </div>
                <div class="form-group">
                    <label class="form-label">${t('Nueva contraseña (mínimo 8)')}</label>
                    <input type="password" class="form-input" id="recPass" autocomplete="new-password">
                </div>
                <div class="form-group">
                    <label class="form-label">${t('Repetí la contraseña')}</label>
                    <input type="password" class="form-input" id="recPass2" autocomplete="new-password">
                </div>
            </div>
            <div class="login-error" id="recError" style="display:none"></div>
            <div id="recOk" style="display:none; color:var(--accent-green,#16a34a); font-size:13px; margin:8px 0"></div>
            <button class="btn btn-primary btn-full" id="recAccion">${t('Enviarme el código')}</button>
            <button type="button" class="btn btn-full" id="recVolver"
                style="background:transparent; border:none; color:var(--text-secondary); margin-top:8px; cursor:pointer">
                ${t('Volver al ingreso')}
            </button>`;

        let fase = 'solicitar'; // 'solicitar' → 'confirmar'
        let enProgreso = false;
        const errEl = document.getElementById('recError');
        const okEl = document.getElementById('recOk');
        const accionBtn = document.getElementById('recAccion');

        const volver = () => {
            panel.style.display = 'none';
            panel.innerHTML = '';
            form.style.display = '';
        };
        document.getElementById('recVolver').addEventListener('click', volver);

        accionBtn.addEventListener('click', async () => {
            if (enProgreso) return;
            errEl.style.display = 'none';
            okEl.style.display = 'none';
            const emailVal = (document.getElementById('recEmail').value || '').trim().toLowerCase();
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
                errEl.textContent = t('Ingresá un correo válido');
                errEl.style.display = 'block';
                return;
            }
            enProgreso = true;
            accionBtn.disabled = true;
            try {
                if (fase === 'solicitar') {
                    await fetch('/api/admin/onboarding/solicitar-codigo', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: emailVal }),
                    });
                    // Respuesta uniforme del backend: avanzamos siempre (si la cuenta
                    // existe, el código llegó; si no, el código nunca va a validar).
                    document.getElementById('recPaso2').style.display = '';
                    okEl.textContent = t('Si el correo corresponde a una cuenta, te llegó un código (revisá spam).');
                    okEl.style.display = 'block';
                    accionBtn.textContent = t('Guardar contraseña nueva');
                    fase = 'confirmar';
                } else {
                    const codigo = (document.getElementById('recCodigo').value || '').trim();
                    const password = document.getElementById('recPass').value || '';
                    const password2 = document.getElementById('recPass2').value || '';
                    if (!codigo) {
                        errEl.textContent = t('Ingresá el código que te llegó por correo');
                        errEl.style.display = 'block';
                        return;
                    }
                    if (password.length < 8) {
                        errEl.textContent = t('La contraseña debe tener al menos 8 caracteres');
                        errEl.style.display = 'block';
                        return;
                    }
                    if (password !== password2) {
                        errEl.textContent = t('Las contraseñas no coinciden');
                        errEl.style.display = 'block';
                        return;
                    }
                    const res = await fetch('/api/admin/onboarding/crear-contrasena', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: emailVal, codigo, password, passwordRepetida: password2 }),
                    });
                    const data = await res.json();
                    if (!res.ok) {
                        errEl.textContent = data.error || t('No se pudo completar la operación');
                        errEl.style.display = 'block';
                        return;
                    }
                    volver();
                    const loginError = document.getElementById('loginError');
                    if (loginError) {
                        loginError.textContent = t('✓ Contraseña lista. Ingresá con tu correo y tu contraseña nueva.');
                        loginError.style.color = 'var(--accent-green,#16a34a)';
                        loginError.style.display = 'block';
                    }
                }
            } catch (e) {
                errEl.textContent = t('Error de conexión');
                errEl.style.display = 'block';
            } finally {
                enProgreso = false;
                accionBtn.disabled = false;
            }
        });
    },

    async logout() {
        const token = this.getToken();
        if (token) {
            try {
                await fetch(ENDPOINTS.ADMIN_LOGOUT, {
                    method: 'POST',
                    headers: { 'X-Admin-Token': token },
                });
            } catch (e) {
                // Ignorar errores de logout
            }
        }
        this.clearToken();
        this.currentUser = null;
        this.showLogin();
    },

    // Timeout default de fetchWithAuth. Callers con operaciones más lentas (reportes/
    // cascadas sobre rangos largos) pasan su propio timeoutMs como 4to argumento.
    FETCH_TIMEOUT_MS: 60000,

    // Helper para hacer fetch con token (retry automático si el servidor está reiniciándose).
    // timeoutMs: override por caller para operaciones legítimamente más lentas que el default.
    async fetchWithAuth(url, options = {}, _retries = 2, timeoutMs = this.FETCH_TIMEOUT_MS) {
        // Selector de moneda del panel: se agrega a las lecturas que lo entienden
        // (SSOT de la lista en moneda-vista-en-requests.js — hay DOS capas de red
        // en este front y ambas deben mandarlo).
        url = conMonedaDeVista(url, options.method);
        const token = this.getToken();
        const headers = {
            ...options.headers,
            'X-Admin-Token': token,
        };
        // Normalización del body (corrige el origen — el backend además tiene la
        // red universal text/plain→JSON). FormData se manda tal cual (el browser
        // pone su propio Content-Type con boundary). Un objeto se serializa. Un
        // string ya serializado igual lleva el header JSON: sin esto, un caller
        // que hace JSON.stringify a mano mandaba text/plain y fastify no parseaba
        // → request.body string → handlers con campos undefined → 400 engañoso.
        if (options.body != null && !(options.body instanceof FormData)) {
            if (typeof options.body === 'object') {
                options.body = JSON.stringify(options.body);
            }
            if (!headers['Content-Type'] && !headers['content-type']) {
                headers['Content-Type'] = 'application/json';
            }
        }

        const timeoutController = new AbortController();
        const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
        // Señal del caller (ej. cancelar un preview obsoleto al cambiar de nodo/fecha) +
        // señal del timeout interno: cualquiera de las dos debe abortar el fetch. Sin
        // combinarlas, un signal externo en options quedaba PISADO por el de acá abajo —
        // el caller creía que estaba cancelando la red y en realidad no cortaba nada.
        // Combinación manual (no AbortSignal.any: soporte de navegador no garantizado en
        // este helper de 163+ call-sites) — un solo controller que se dispara con lo que
        // ocurra primero entre el signal del caller y el timeout interno.
        const combinedController = new AbortController();
        if (options.signal) {
            if (options.signal.aborted) combinedController.abort();
            else options.signal.addEventListener('abort', () => combinedController.abort(), { once: true });
        }
        timeoutController.signal.addEventListener('abort', () => combinedController.abort(), { once: true });
        const signal = combinedController.signal;
        const abortadoPorCaller = () => !!options.signal && options.signal.aborted;

        let res;
        try {
            res = await fetch(url, { ...options, headers, signal });
        } catch (networkError) {
            if (networkError.name === 'AbortError') {
                // Abort del CALLER (no nuestro timeout): el caller ya sabe por qué canceló
                // (nodo/fecha cambiaron) — no es un timeout de red, no reintentar, el caller
                // decide qué hacer (típicamente: ignorar, la llamada nueva ya está en curso).
                if (abortadoPorCaller()) throw networkError;
                // AbortError por nuestro timeout = el backend no responde: NO reintentar (ya se
                // esperó timeoutMs completos), lanzar con code='TIMEOUT' para el caller.
                const timeoutError = new Error(`Timeout tras ${timeoutMs}ms: ${url}`);
                timeoutError.code = 'TIMEOUT';
                throw timeoutError;
            }
            // Fallo de red (servidor caído) — reintentar
            if (_retries > 0) {
                await new Promise(r => setTimeout(r, 1500));
                return this.fetchWithAuth(url, options, _retries - 1, timeoutMs);
            }
            throw networkError;
        } finally {
            clearTimeout(timeoutId);
        }

        // Si la sesión expiró, volver al login
        if (res.status === 401) {
            this.clearToken();
            this.showLogin();
            throw new Error('Session expired');
        }

        // Respuesta no-JSON (HTML de Cloudflare durante restart) — reintentar
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json') && _retries > 0) {
            await new Promise(r => setTimeout(r, 1500));
            return this.fetchWithAuth(url, options, _retries - 1, timeoutMs);
        }

        return res.json();
    },

    // ---- NAVIGATION ----
    setupNavigation() {
        document.querySelectorAll('.nav-item').forEach(item => {
            item.addEventListener('click', () => {
                this.navigate(item.dataset.view);
            });
        });
    },

    navigate(view) {
        // Detener polling de dispositivos al salir de Plataforma. Solo si el
        // módulo ya se cargó antes (lazy) — si el usuario nunca entró a
        // Plataforma:Dispositivos no hay timer que limpiar, y forzar el import
        // acá en CADA navegación anularía el lazy-load.
        getModuloSiYaCargado('platformDispositivos')?._stopPolling();
        // Al salir del dashboard, marcar que su panel activo debe re-pintarse al
        // volver (el DOM se recrea al cambiar de vista; sin esto el panel
        // Promos/Sorteios/Red quedaba vacío al regresar de otra sección).
        if (this.currentView === 'command-center' && view !== 'command-center') {
            this._dashPanelPintado = false;
        }
        this.currentView = view;
        document.querySelectorAll('.nav-item').forEach(i => i.classList.remove('active'));
        document.querySelector(`.nav-item[data-view="${view}"]`)?.classList.add('active');
        document.querySelectorAll('.view').forEach(v => v.classList.remove('active'));
        document.getElementById(`view-${view}`)?.classList.add('active');
        document.getElementById('pageTitle').textContent =
            {
                'command-center': t('Dashboard'),
                juegos: t('Juegos'),
                plataforma: t('Plataforma'),
                clientes: t('Clientes'),
                actividad: t('Operación'),
                finanzas: t('Finanzas'),
                marketing: t('Marketing'),
                sistema: t('Auditoría'),
            }[view] || view;
        this.loadView(view);
        // En móvil, al elegir una vista se cierra el sidebar (y su backdrop).
        this.cerrarSidebar();
    },

    // ---- TAB SWITCHING ----
    switchTab(view, tab) {
        const viewEl = document.getElementById(`view-${view}`);
        if (!viewEl) return;

        // Update tab buttons. Solo botones CON data-tab: algunas vistas (ej. Finanzas → Cierres) anidan
        // más de un .view-tabs adentro (los tabs principales + sub-tabs propios como Panorama/Cortes/
        // Liquidações, que usan data-subtab, no data-tab). Sin este filtro, btn.dataset.tab === tab daba
        // undefined === 'cierres' → false para los sub-tabs → les apagaba el resaltado 'active' cada vez
        // que se entraba a Cierres, aunque el usuario nunca los hubiera tocado (bug reportado: "Visão
        // Geral" nunca aparecía marcado al entrar al tab).
        viewEl.querySelectorAll('.view-tabs .tab-btn[data-tab]').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.tab === tab);
        });

        // Update tab content
        viewEl.querySelectorAll('.tab-content').forEach(content => {
            const isActive = content.id === `tab-${view}-${tab}`;
            content.classList.toggle('active', isActive);
            content.style.display = isActive ? '' : 'none';
        });

        // Load data for the tab
        this.loadTabData(view, tab);
    },

    switchLotowheelSubtab(subtab) {
        const container = document.getElementById('tab-juegos-lotowheel');
        if (!container) return;

        // Update subtab buttons
        container.querySelectorAll('.subtabs .tab-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.subtab === subtab);
        });

        // Update subtab content
        container.querySelectorAll('.subtab-content').forEach(content => {
            const isActive = content.id === `subtab-${subtab}`;
            content.classList.toggle('active', isActive);
            content.style.display = isActive ? 'block' : 'none';
        });

        // Load data for the subtab
        if (subtab === 'publicos') {
            LotowheelModule.loadLotowheelConfigs();
        } else if (subtab === 'virtuales') {
            LotowheelModule.loadVirtualDraws();
        }
    },

    async loadTabData(view, tab) {
        // Detener polling de dispositivos al salir de su tab. Solo si el módulo
        // ya se cargó antes (ver navigate() — mismo criterio: no forzar el
        // import de un módulo que el usuario nunca visitó).
        if (`${view}:${tab}` !== 'plataforma:dispositivos') {
            getModuloSiYaCargado('platformDispositivos')?._stopPolling();
        }

        // Módulo pesado que este view:tab necesita (MODULO_POR_TAB, arriba de la
        // clase) — se resuelve solo ese, no todos. Los que antes corrían init() en
        // el bootstrap usan getModuloConInit (lo corre una sola vez, recién la
        // primera vez que se resuelven — ver showApp()).
        const infoModulo = MODULO_POR_TAB[`${view}:${tab}`];
        const Modulo =
            infoModulo && (await (infoModulo.conInit ? getModuloConInit(infoModulo.key) : getModulo(infoModulo.key)));

        switch (`${view}:${tab}`) {
            // Plataforma
            case 'plataforma:red':
                Modulo.load();
                break;
            case 'plataforma:entidades':
                Modulo.load();
                break;
            // Saldos de la Red ahora vive en Finanzas
            case 'finanzas:saldos':
                cargarSaldosDeRedOperativa();
                break;
            case 'plataforma:dispositivos':
                Modulo.load();
                break;
            // Juegos - Dynamic Game Loading
            case 'juegos:lotowheel':
                // Cargar subtab activa (por defecto: publicos)
                this.switchLotowheelSubtab('publicos');
                break;
            case 'juegos:digi12': {
                const Digi12 = await getModulo('digi12');
                Digi12.load();
                break;
            }
            case 'juegos:tickets':
                Modulo.loadTickets();
                break;
            case 'juegos:pozos-comunes':
                Modulo.loadPozos();
                break;
            // Juegos futuros: El patron es juegos:{code}
            // Cuando se agregue Bingo: case 'juegos:bingo': BingoModule.load(); break;
            // Clientes
            case 'clientes:jugadores':
                Modulo.loadJugadores();
                break;
            case 'clientes:bloqueos':
                LotowheelModule.loadBloqueos();
                break;
            // Finanzas (tabs: saldos, pasarelas, transferencias, cierres, alertas)
            case 'finanzas:pasarelas':
                // El Gateway abre en su sub-tab Transacciones; Provedores se carga al cambiar de sub-tab.
                Modulo.loadPasarelas();
                break;
            case 'finanzas:transferencias':
                Modulo.loadAdelantos();
                break;
            case 'finanzas:b2b':
                Modulo.load();
                break;
            // Operación: Wallet = movimientos de wallet (canal digital) — módulo finanzas, UI mudada acá.
            case 'actividad:wallet':
                Modulo.loadMovimientos();
                break;
            case 'finanzas:cierres':
                // Carga perezosa (§ ver switchCierresSubtab/montarTimelineMaestraCierres en finanzas.js): solo
                // Panorama General (liviano) carga al entrar. Cortes/Liquidaciones cargan recién al abrir su
                // sub-tab — antes acá se disparaban los 3 fetch completos siempre, aunque no se miraran.
                Modulo.poblarSelectorRelatorioFechamento();
                Modulo.montarTimelineMaestraCierres();
                break;
            case 'finanzas:alertas':
                Modulo.loadAlertas();
                Modulo.loadPagosExternos();
                Modulo.loadRoundsInstantaneosEnRevision();
                break;
            // Sistema
            case 'sistema:auditoria':
                Modulo.loadAuditoria();
                break;
            case 'sistema:sesiones':
                Modulo.loadSesiones();
                break;
            case 'sistema:series-tickets':
                Modulo.loadSeriesTickets();
                break;
            case 'sistema:fleet-crashes':
                Modulo.loadFleetCrashes();
                break;
            case 'sistema:fleet-whatsapp':
                Modulo.loadFleetWhatsapp();
                break;
            // Actividad
            case 'actividad:tickets':
                Modulo.loadTickets();
                break;
            case 'actividad:integradores':
                Modulo.loadIntegradores();
                break;
            case 'marketing:promociones':
                Modulo.loadPromociones();
                break;
            case 'marketing:analisis':
                Modulo.loadAnalisis();
                break;
            case 'marketing:presets':
                Modulo.loadPresets();
                break;
            case 'marketing:cupones':
                Modulo.loadCampanas();
                break;
            case 'marketing:tickets':
                Modulo.loadTickets();
                break;
            case 'marketing:referidos':
                Modulo.loadReferidos();
                break;
            default: {
                const editor = view === 'juegos' && this.editoresInstant?.find(e => e.gameCode === tab);
                if (editor) {
                    const productos = await getModulo('productosInstant');
                    await productos.load(tab, editor.contrato, document.getElementById(`tab-juegos-${tab}`));
                }
            }
        }
    },

    toggleSidebar() {
        const abierto = document.getElementById('sidebar').classList.toggle('open');
        // El backdrop sigue el estado del sidebar: visible cuando está abierto.
        document.getElementById('sidebarBackdrop')?.classList.toggle('visible', abierto);
    },

    // Cierra el sidebar (tocar el backdrop en móvil). Idéntico al cierre que ya
    // hace navigate() al elegir una vista — centralizado para reusar.
    cerrarSidebar() {
        document.getElementById('sidebar').classList.remove('open');
        document.getElementById('sidebarBackdrop')?.classList.remove('visible');
    },

    async loadView(view) {
        switch (view) {
            case 'command-center':
                this._initTimeline();
                this.loadCommandCenter();
                break;
            case 'juegos':
                // Dynamic Game Loading: Cargar el primer game_type disponible
                if (this.gameTypes && this.gameTypes.length > 0) {
                    const firstGameType = this.gameTypes[0].code;
                    this.switchTab('juegos', firstGameType);
                } else {
                    // Fallback a lotowheel si no hay game_types cargados
                    this.switchLotowheelSubtab('publicos');
                }
                break;
            case 'plataforma': {
                // Cargar la tab activa (por defecto: red operativa) — lazy (ver loadTabData).
                const PlatformNodos = await getModulo('platformNodos');
                PlatformNodos.load();
                break;
            }
            case 'clientes': {
                // Cargar jugadores (tab por defecto) — lazy (ver loadTabData).
                const ClientesModule = await getModuloConInit('clientes');
                ClientesModule.loadJugadores();
                break;
            }
            case 'finanzas':
                // Tab por defecto: Saldos
                cargarSaldosDeRedOperativa();
                break;
            case 'sistema': {
                // Tab por defecto: auditoría
                const SistemaModule = await getModuloConInit('sistema');
                SistemaModule.loadAuditoria();
                break;
            }
            case 'actividad': {
                // Tab por defecto de Operación: Tickets (pasarelas/wallets se
                // movieron a Finanzas).
                const ActividadModule = await getModuloConInit('actividad');
                ActividadModule.loadTickets();
                break;
            }
            case 'marketing': {
                const MarketingModule = await getModuloConInit('marketing');
                MarketingModule.loadPromociones();
                break;
            }
        }
    },

    // ---- MODAL ----
    setupModalTabs() {
        document.querySelectorAll('.modal-tab').forEach(tab => {
            // Tabs con data-modal son gestionados por TabbedModal via event
            // delegation en event-bindings.js — no registrar listener duplicado.
            if (tab.dataset.modal) return;
            tab.addEventListener('click', () => {
                const modal = tab.closest('.modal');
                modal.querySelectorAll('.modal-tab').forEach(t => t.classList.remove('active'));
                modal.querySelectorAll('.tab-panel').forEach(p => p.classList.remove('active'));
                tab.classList.add('active');
                document.getElementById(`panel-${tab.dataset.tab}`)?.classList.add('active');

                // Hook: cargar panel de accesos al abrirlo (solo si aplica)
                if (tab.dataset.tab === 'acceso' && modal.id === 'modalLotowheelConfig') {
                    const sorteoId = parseInt(document.getElementById('lotowheelConfigId').value) || null;
                    LotowheelModule.loadAccesoPanel(sorteoId);
                }
                // Hook: cargar panel de pozos comunes al abrirlo
                if (tab.dataset.tab === 'pozos-comunes' && modal.id === 'modalLotowheelConfig') {
                    const sorteoId = parseInt(document.getElementById('lotowheelConfigId').value) || null;
                    LotowheelModule.loadPozoComunesPanel(sorteoId);
                }
            });
        });

        // Toggle colapsable del grupo Avanzado (colapsado por defecto)
        const sepAvanzado = document.getElementById('tabSepAvanzado');
        const groupAvanzado = document.getElementById('tabGroupAvanzado');
        if (sepAvanzado && groupAvanzado) {
            sepAvanzado.addEventListener('click', () => {
                const collapsed = sepAvanzado.classList.toggle('collapsed');
                groupAvanzado.classList.toggle('collapsed', collapsed);
            });
        }
    },

    openModal(id) {
        document.getElementById('modalOverlay').classList.add('open');
        document.querySelectorAll('.modal').forEach(m => (m.style.display = 'none'));
        document.getElementById(id).style.display = 'flex';
        // Iconos ya son <img> locales, no requiere inicializacion
    },

    closeModal() {
        document.getElementById('modalOverlay').classList.remove('open');
    },

    /**
     * Muestra el modal de estadísticas con contenido formateado
     * @param {string} title - Título del modal
     * @param {string} content - Contenido a mostrar (texto plano)
     */
    showStatsModal(title, content) {
        document.getElementById('modalStatsTitle').textContent = title;
        document.getElementById('modalStatsContent').textContent = content;
        this.openModal('modalStats');
    },

    /**
     * Muestra un modal genérico con contenido HTML dinámico
     * @param {string} title - Título del modal
     * @param {string} htmlContent - Contenido HTML a mostrar
     * @param {Object} options - Opciones: { hideFooter: true } para ocultar Copiar/Cerrar
     */
    showGenericModal(title, htmlContent, options = {}) {
        document.getElementById('modalGenericTitle').textContent = title;
        const content = document.getElementById('modalGenericContent');
        content.innerHTML = htmlContent;
        // tabbed:true → display:contents para que TabbedModal (sidebar + panel)
        // participe del flex de .modal-body. Default block + padding para texto.
        if (options.tabbed) {
            content.style.display = 'contents';
            content.style.padding = '0';
        } else {
            content.style.display = 'block';
            content.style.padding = '20px';
        }
        const footer = document.getElementById('modalGenericFooter');
        if (footer) {
            footer.style.display = options.hideFooter ? 'none' : 'flex';
        }
        this.openModal('modalGeneric');
    },

    async copyGenericContentToClipboard() {
        const content = document.getElementById('modalGenericContent').innerText;
        try {
            await navigator.clipboard.writeText(content);
            this.toast(t('Copiado al portapapeles'));
        } catch (e) {
            this.toast(t('Error al copiar'), 'error');
        }
    },

    /**
     * Copia el contenido del modal de stats al clipboard
     */
    async copyStatsToClipboard() {
        const content = document.getElementById('modalStatsContent').textContent;
        try {
            await navigator.clipboard.writeText(content);
            this.toast(t('Copiado al portapapeles'));
        } catch (e) {
            // Fallback para navegadores sin soporte de clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = content;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            this.toast(t('Copiado al portapapeles'));
        }
    },

    // ---- TOAST ----
    toast(msg, type = 'success') {
        const container = document.getElementById('toastContainer');
        const toast = document.createElement('div');
        toast.className = `toast ${type}`;
        toast.textContent = msg;
        container.appendChild(toast);
        setTimeout(() => toast.remove(), 3000);
    },

    // ---- UTILS ----
    formatMoney(cents, moneda) {
        return MonedaService.format(cents, moneda);
    },

    formatTime(seconds) {
        if (!seconds || seconds < 0) return '--:--';
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    },

    // Las fechas que SQLite genera con datetime('now') llegan como
    // "YYYY-MM-DD HH:MM:SS" SIN marcador de zona, pero el server corre en UTC,
    // así que ese valor ES UTC. Sin la "Z", new Date() lo interpreta como hora
    // local del navegador y desfasa (ej: la ficha de pasarela mostraba "Expira"
    // 5h antes que "Solicitud" porque expires_at sí traía Z y created_at no).
    // Normalizamos las naive a UTC explícito; las que ya traen Z u offset se
    // dejan intactas.
    _normalizarFechaUtc(dateString) {
        if (typeof dateString !== 'string') return dateString;
        const esNaiveSqlite = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(dateString.trim());
        if (!esNaiveSqlite) return dateString;
        return dateString.trim().replace(' ', 'T') + 'Z';
    },

    formatDate(dateString) {
        if (!dateString) return '-';
        try {
            const date = new Date(this._normalizarFechaUtc(dateString));
            // TODO Fase 23 (locale formatting por contexto): el locale aquí
            // debería venir del nodo del operador, no hardcoded a brand peruana.
            return date.toLocaleDateString('es-PE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    },

    // ---- TIMELINE ----
    _initTimeline() {
        const container = document.getElementById('ccTimelineBar');
        if (!container || container.dataset.initialized) return;
        container.dataset.initialized = '1';
        TimelineBar.init(container, (desde, hasta, preset) => {
            this._timelineDesde = desde;
            this._timelineHasta = hasta;
            this.loadCommandCenter();
            // El dashboard Red/Sorteos SÍ se refresca al cambiar el rango
            // (disparador legítimo, no el polling). Solo si su tab está visible.
            if (this._dashTabActivo === 'red') this._renderDashRed();
            if (this._dashTabActivo === 'sorteos') this._renderDashSorteosKpis();
        });
        // El timeline aplica su preset inicial (1M) SIN emitir → leer sus fechas
        // ahora para que el primer render del dashboard ya use el rango del mes,
        // en vez de quedar con rango null (acumulado/vacío) hasta el primer click.
        const fechasIni = TimelineBar.getFechas();
        this._timelineDesde = fechasIni.desde;
        this._timelineHasta = fechasIni.hasta;
    },

    _timelineDesde: null,
    _timelineHasta: null,

    // ---- COMMAND CENTER ----
    async loadCommandCenter() {
        // Guard anti-race: el polling y los cambios de preset disparan cargas
        // concurrentes. Si el usuario cambia rápido de rango (AÑO→1M→7D), las
        // respuestas pueden volver fuera de orden y pintar KPIs del rango viejo.
        // "Última request gana": cada llamada toma un token; al volver, si ya se
        // disparó otra carga más nueva, esta respuesta se descarta sin pintar.
        const miToken = (this._commandCenterToken || 0) + 1;
        this._commandCenterToken = miToken;
        const data = await AdminApiClient.getCommandCenter(
            this.sorteoRange,
            this.sorteoConfigFilter,
            this._timelineDesde,
            this._timelineHasta
        );
        if (this._commandCenterToken !== miToken) return;
        if (!data) return;

        // Vista operativa scoped (solo llega para usuarios no-superadmin):
        // hero "Minha rede" + ação necessária + saldo + tendência 7d.
        this._renderVistaOperativa(data.vistaOperativa || null);

        const hoy = data.hoy || {};
        // null = subárbol con monedas mixtas sin tasa de cambio: hueco honesto con
        // aviso (title), nunca un 0 creíble y falso. ≈ cuando el total se consolidó
        // con tasas (flags moneda_meta / moneda_meta_produccion del backend).
        const pintarKpiDinero = (el, valor, meta, campoDelDesglose) => {
            if (!el) return;
            // La moneda la manda el backend en `moneda_meta.moneda`: es la del selector
            // cuando el operador eligió una. Formatear sin ella usaba el símbolo del
            // contexto del nodo, así que un total ya consolidado a soles se pintaba
            // como reales (bug visto en prod 2026-07-19).
            const monedaDelTotal = meta?.moneda || undefined;
            if (valor === null) {
                el.textContent = '';
                el.title = '';
                const marca = DesgloseMonedaPopover.marcaHtml(meta, campoDelDesglose, { hueco: true });
                if (marca) {
                    el.innerHTML = marca;
                } else {
                    el.textContent = '—';
                    el.title = t('Rede com moedas mistas e sem taxas de câmbio carregadas — total não confiável');
                }
                return;
            }
            el.title = '';
            const importe = this.formatMoney(valor || 0, monedaDelTotal);
            // El ≈ es pulsable cuando hay desglose: abre el detalle por moneda con su
            // tasa, para que el consolidado no haya que creerlo a ciegas.
            const marca = meta?.consolidado_aproximado ? DesgloseMonedaPopover.marcaHtml(meta, campoDelDesglose) : '';
            if (marca) {
                el.innerHTML = `${marca} ${escapeHtmlParaInyeccionSegura(importe)}`;
            } else {
                el.textContent = (meta?.consolidado_aproximado ? '≈ ' : '') + importe;
            }
        };
        const metaCaja = hoy.moneda_meta || {};
        const metaProduccion = hoy.moneda_meta_produccion || {};
        pintarKpiDinero(document.getElementById('kpiIngresado'), hoy.ingresado, metaCaja, [
            'ingresadoEfectivo',
            'ingresadoPasarela',
        ]);
        pintarKpiDinero(document.getElementById('kpiCobrado'), hoy.cobrado, metaCaja, 'cobrado');
        pintarKpiDinero(document.getElementById('kpiRejeitados'), hoy.rejeitados, metaCaja, 'rejeitados');
        pintarKpiDinero(document.getElementById('kpiCompensados'), hoy.compensados, metaCaja, 'compensados');
        pintarKpiDinero(document.getElementById('kpiEnVuelo'), hoy.enVuelo, metaCaja, 'enVuelo');
        pintarKpiDinero(document.getElementById('kpiApostado'), hoy.totalApostado, metaProduccion, 'jugado');
        pintarKpiDinero(document.getElementById('kpiPagado'), hoy.totalPagado, metaProduccion, 'pagado');

        const kpiMargen = document.getElementById('kpiMargen');
        if (hoy.margen === null) {
            kpiMargen.textContent = '—';
            kpiMargen.title = t('Rede com moedas mistas e sem taxas de câmbio carregadas — total não confiável');
            kpiMargen.className = 'kpi-value';
        } else {
            const margen = hoy.margen || 0;
            kpiMargen.title = '';
            // GGR es un DERIVADO (jugado − pagado), pero sus dos insumos viajan nativos por
            // moneda en el mismo desglose: se piden con '-pagado' para que el detalle muestre
            // el GGR real de cada moneda, sin convertir. Y la moneda va a formatMoney —
            // sin ella un total ya consolidado se pintaba con el símbolo del tenant.
            const signo = margen >= 0 ? '+' : '';
            const importe = this.formatMoney(margen, metaProduccion?.moneda || undefined);
            const marca = metaProduccion?.consolidado_aproximado
                ? DesgloseMonedaPopover.marcaHtml(metaProduccion, ['jugado', '-pagado'])
                : '';
            if (marca) {
                kpiMargen.innerHTML = `${signo}${marca} ${escapeHtmlParaInyeccionSegura(importe)}`;
            } else {
                kpiMargen.textContent = signo + (metaProduccion.consolidado_aproximado ? '≈ ' : '') + importe;
            }
            kpiMargen.className = 'kpi-value ' + (margen >= 0 ? 'green' : 'red');
        }

        document.getElementById('kpiRTP').textContent = hoy.retornoPct === null ? '—' : (hoy.retornoPct || 0) + '%';
        // Presencia separada: jugadores vs aparatos (TV/POS). El conteo viejo
        // mezclaba ambos en un solo número inflado.
        const pres = data.presencia || {};
        const aparatos = pres.aparatosPorTipo || {};
        const elJug = document.getElementById('ccJugadoresOnline');
        const elAp = document.getElementById('ccAparatosOnline');
        const elDesg = document.getElementById('ccAparatosDesglose');
        if (elJug) elJug.textContent = pres.jugadoresOnline || 0;
        if (elAp) elAp.textContent = pres.aparatosOnline || 0;
        // Desglose TV/POS solo si hay aparatos (evita "(0 TV · 0 POS)" ruidoso).
        if (elDesg) {
            const total = aparatos.total || 0;
            elDesg.textContent = total > 0 ? `(${aparatos.tv || 0} TV · ${aparatos.pos || 0} POS)` : '';
        }
        document.getElementById('ccSorteosHoy').textContent = hoy.numSorteos || 0;

        const proximo = data.proximoSorteo;
        document.getElementById('ccProximoSorteo').textContent = proximo
            ? `#${proximo.numSorteo} en ${proximo.segundosRestantes}s`
            : '--';

        // Guardar configs y popular dropdown si hay más de una
        if (data.sorteoConfigs && data.sorteoConfigs.length > 0) {
            this.sorteoConfigs = data.sorteoConfigs;
            this.populateConfigDropdown();
        }

        this.currentSorteos = data.sorteos || [];
        // Flags del consolidado multi-moneda de las filas miRed (backend):
        // ≈ si hubo conversión, hueco si faltó tasa. Sin flags no se puede
        // distinguir "consolidado exacto" de "no sé" y se pintaría sin aviso.
        this.miRedMonedaMeta = data.miRedMonedaMeta || null;

        // Inicializar FuzzySearch para sorteos (busca en múltiples campos)
        this.sorteoSearcher = new FuzzySearch(
            this.currentSorteos.map(s => ({
                ...s,
                // Campos extra para búsqueda
                _numSorteoStr: `#${s.numSorteo}`,
                _numeroStr: `${s.numero}`,
                // Filas scoped (no-superadmin) no traen dinero global: se busca
                // sobre vendido/premiado de la red del usuario (miRed). El null
                // de "sin consolidar" cae a 0 SOLO acá (string de búsqueda, no UI).
                _apostadoStr: this.formatMoney(s.miRed ? (s.miRed.vendido ?? 0) : s.totalApostado),
                _pagadoStr: this.formatMoney(s.miRed ? (s.miRed.premiadoCobrado ?? 0) : s.totalPagado),
                _configName: s.configName || '',
            })),
            ['_numSorteoStr', '_numeroStr', '_apostadoStr', '_pagadoStr', '_configName'],
            { maxResults: 500 }
        );

        this.filterSorteos();

        // Dashboard de RED (tab por defecto): analítica "quién produce y quién no".
        // Se pinta UNA vez (primer load) — NO en cada tick del polling: loadCommandCenter
        // corre en un setInterval, y re-renderizar los charts en cada tick los destruía
        // y recreaba (parpadeo "refresco constante"). El refresco real del dashboard Red
        // lo disparan: el click de tab y el cambio del timeline (ver _initDashTabs y el
        // onChange del timeline-bar). El polling solo refresca KPIs/sorteos.
        this._initDashTabs();
        // Pintar el contenido del tab ACTIVO al entrar al dashboard. Antes solo
        // cubría 'red' la 1ª vez → al volver de otra sección con Promos/Sorteios
        // activos, esos paneles quedaban vacíos (el DOM se recreó y nada los
        // re-disparaba). El guard _dashPanelPintado evita re-pintar en cada tick
        // del polling (que también llama loadCommandCenter) — solo al (re)entrar.
        if (!this._dashPanelPintado) {
            this._dashPanelPintado = true;
            if (this._dashTabActivo === 'red') this._renderDashRed();
            else if (this._dashTabActivo === 'promos') this._renderDashPromos();
            else if (this._dashTabActivo === 'sorteos') this._renderDashSorteosKpis();
        }
    },

    // Tab activo del dashboard ('red' por defecto). El switch oculta/muestra los
    // paneles y pinta la Red on-demand.
    _dashTabActivo: 'red',
    _initDashTabs() {
        const tabs = document.querySelectorAll('.cc-dash-tab');
        if (!tabs.length || this._dashTabsInit) return;
        this._dashTabsInit = true;

        // El tab "Promos" del dashboard es un atajo al mismo dato de Marketing —
        // debe respetar la misma capability que oculta esa sección del sidebar
        // (view.marketing), o un usuario sin acceso a Marketing lo ve igual acá.
        if (!this.can('view.marketing')) {
            const tabPromos = document.querySelector('.cc-dash-tab[data-cc-tab="promos"]');
            if (tabPromos) tabPromos.style.display = 'none';
            if (this._dashTabActivo === 'promos') this._dashTabActivo = 'red';
        }
        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const destino = tab.dataset.ccTab;
                this._dashTabActivo = destino;
                document.querySelectorAll('.cc-dash-tab').forEach(t2 => t2.classList.toggle('active', t2 === tab));
                document.querySelectorAll('.cc-dash-panel').forEach(p => {
                    p.style.display = p.dataset.ccPanel === destino ? '' : 'none';
                });
                if (destino === 'red') this._renderDashRed();
                if (destino === 'promos') this._renderDashPromos();
                if (destino === 'sorteos') this._renderDashSorteosKpis();
            });
        });
    },

    // Comparativa por sorteo (jugadas/premios/RTP) arriba del listado operativo.
    // Respeta el rango del timeline del dashboard.
    async _renderDashSorteosKpis() {
        const cont = document.getElementById('ccSorteosKpis');
        if (!cont) return;
        const { DashboardSorteosKpisCharts } = await import('./components/dashboard-sorteos-kpis-charts.js');
        DashboardSorteosKpisCharts.render(cont, this._timelineDesde, this._timelineHasta);
    },

    // Nodo raíz de la red para superadmin (no tiene nodo_id propio). El backend lo
    // expone en el bootstrap; fallback al raíz del árbol (parent_id NULL).
    _NODO_RAIZ_RED: 1,
    async _renderDashRed() {
        const cont = document.getElementById('ccDashRed');
        if (!cont) return;
        // Nodo del usuario, o el raíz de la red si es superadmin (nodo_id null).
        const nodoId = (this.currentUser && this.currentUser.nodo_id) || this._NODO_RAIZ_RED;
        const { DashboardRedCharts } = await import('./components/dashboard-red-charts.js');
        DashboardRedCharts.render(cont, nodoId, this._timelineDesde, this._timelineHasta);
    },

    // Resumen de salud de promos en el dashboard general (linkea al detalle en
    // Marketing). Reusa el mismo componente que la tab Análisis, en modo resumen.
    async _renderDashPromos() {
        const cont = document.getElementById('ccDashPromos');
        if (!cont) return;
        if (!this.can('view.marketing')) return; // ver _initDashTabs: mismo gate que el sidebar
        const { MarketingAnalisisPromos } = await import('./components/marketing-analisis-promos.js');
        MarketingAnalisisPromos.renderResumen(cont, () => {
            // Navegar a Marketing y abrir directo el tab Análisis (no el default).
            this.navigate('marketing');
            // switchTab tras montar la vista (navigate puede ser async en su render).
            setTimeout(() => this.switchTab('marketing', 'analisis'), 50);
        });
    },

    /**
     * Sparkline de tendencia 7d con divs inline (sin libs): una barra por día
     * escalada al máximo jugado de la ventana, con el GGR acumulado abajo.
     */
    _renderTarjetaTendencia7d(tendencia7d) {
        if (!tendencia7d.length) return '';
        let maxJugado = 1;
        let ggr7d = 0;
        for (const dia of tendencia7d) {
            if (dia.jugado > maxJugado) maxJugado = dia.jugado;
            ggr7d += dia.ggr || 0;
        }
        const barras = tendencia7d
            .map(dia => {
                const alturaPct = Math.max(4, Math.round((dia.jugado / maxJugado) * 100));
                return `<div title="${dia.fecha}: ${this.formatMoney(dia.jugado)}"
                     style="flex:1;height:${alturaPct}%;background:var(--accent-blue);opacity:0.85;border-radius:2px 2px 0 0"></div>`;
            })
            .join('');
        return `
            <div class="kpi-card">
                <div class="kpi-label">${t('Tendencia 7 días')}</div>
                <div style="display:flex;align-items:flex-end;gap:3px;height:42px;margin-top:6px">${barras}</div>
                <div style="font-size:11px;color:var(--text-secondary);margin-top:4px">${t('GGR 7 días')}: <span style="color:${ggr7d >= 0 ? 'var(--accent-green)' : 'var(--accent-red)'}">${this.formatMoney(ggr7d)}</span></div>
            </div>`;
    },

    /**
     * Tarjeta de saldo operativo con barra de nivel — mismo patrón visual que
     * el acordeón "Saldo / Prepago" de la ficha del nodo (platform-nodos.js
     * _renderAcordeonSaldoOperativo). Solo si el nodo opera en prepago.
     */
    _renderTarjetaSaldoOperativoDeMiRed(saldoOperativo) {
        if (!saldoOperativo || !saldoOperativo.prepago_habilitado) return '';
        const COLOR_POR_NIVEL = {
            normal: 'var(--accent-green)',
            bajo: 'var(--accent-orange)',
            critico: 'var(--accent-red)',
            bloqueado: 'var(--accent-red)',
        };
        const nivel = saldoOperativo.nivel || 'normal';
        const color = COLOR_POR_NIVEL[nivel] || 'var(--accent-green)';
        const pct =
            typeof saldoOperativo.porcentaje === 'number'
                ? Math.max(0, Math.min(100, saldoOperativo.porcentaje))
                : null;
        const barra =
            pct != null
                ? `<div style="height:8px;background:rgba(148,163,184,0.25);border-radius:4px;overflow:hidden;margin:8px 0">
                       <div style="height:100%;width:${pct}%;background:${color};border-radius:4px"></div>
                   </div>`
                : '';
        const limite = saldoOperativo.limite_credito
            ? `<div style="font-size:11px;color:var(--text-secondary)">${t('Límite de crédito')}: ${this.formatMoney(saldoOperativo.limite_credito)}</div>`
            : '';
        const nivelBadge =
            nivel !== 'normal'
                ? ` <span style="color:${color};font-size:10px;text-transform:uppercase">· ${escapeHtmlParaInyeccionSegura(nivel)}</span>`
                : '';
        return `
            <div class="kpi-card">
                <div class="kpi-label">${t('Saldo operativo')}${nivelBadge}</div>
                <div class="kpi-value" style="color:${color}">${this.formatMoney(saldoOperativo.saldo_operativo || 0)}</div>
                ${barra}${limite}
            </div>`;
    },

    /**
     * Las 3 tarjetas de "Acción necesaria". Cada una es clickeable y navega a
     * la vista/tab donde se resuelve la acción; con count 0 queda en verde
     * "Todo en orden" para que el operador sepa que no debe nada.
     */
    _renderTarjetasAccionNecesaria(accionRequerida) {
        const esc = escapeHtmlParaInyeccionSegura;
        const conformidad = accionRequerida.conformidadPendiente || { cortes: 0, liquidaciones: 0 };
        const totalConformidades = (conformidad.cortes || 0) + (conformidad.liquidaciones || 0);
        const ticketsGanadores = accionRequerida.ticketsGanadores || { cantidad: 0, total: 0 };
        const cajasOffline = accionRequerida.cajasOffline || { cantidad: 0, nombres: [] };

        const tarjeta = (count, titulo, detalleHtml, destino) => `
            <div class="kpi-card" role="button" data-action="app:ccOperativoNavegar" data-destino="${destino}"
                 style="cursor:pointer${count > 0 ? ';border-color:var(--accent-orange)' : ''}">
                <div class="kpi-label">${titulo}</div>
                ${
                    count > 0
                        ? `<div class="kpi-value orange">${count}</div>${detalleHtml}`
                        : `<div class="kpi-value green" style="font-size:15px">✓ ${t('Todo en orden')}</div>`
                }
            </div>`;

        const detalleConformidad = `<div style="font-size:11px;color:var(--text-secondary)">${conformidad.cortes || 0} ${t('Cierres')} · ${conformidad.liquidaciones || 0} ${t('Liquidaciones')}</div>`;
        // total === null: la red mezcla monedas sin tasa de cambio — hueco honesto,
        // nunca un R$0 creíble y falso. ≈ cuando el total se consolidó con tasas.
        const metaTicketsGanadores = ticketsGanadores.moneda_meta || {};
        const totalTicketsHtml =
            ticketsGanadores.total === null
                ? t('Sin consolidar (varias monedas)')
                : (metaTicketsGanadores.consolidado_aproximado ? '≈ ' : '') +
                  this.formatMoney(ticketsGanadores.total || 0);
        const detalleTickets = `<div style="font-size:11px;color:var(--text-secondary)">${totalTicketsHtml}</div>`;
        const detalleCajas = `<div style="font-size:11px;color:var(--text-secondary)">${(cajasOffline.nombres || []).map(esc).join(', ')}</div>`;

        return (
            tarjeta(totalConformidades, t('Conformidades pendientes'), detalleConformidad, 'finanzas-cierres') +
            tarjeta(
                ticketsGanadores.cantidad || 0,
                t('Tickets ganadores por pagar'),
                detalleTickets,
                'actividad-tickets'
            ) +
            tarjeta(cajasOffline.cantidad || 0, t('Cajas offline'), detalleCajas, 'plataforma-dispositivos')
        );
    },

    /**
     * Vista operativa scoped del dashboard (solo llega para no-superadmin).
     * Hero "Mi red" + fila "Acción necesaria" (3 tarjetas clickeables) +
     * saldo operativo con barra de nivel + sparkline de tendencia 7d.
     */
    _renderVistaOperativa(vista) {
        const contenedor = document.getElementById('ccOperativo');
        if (!contenedor) return;
        if (!vista) {
            contenedor.style.display = 'none';
            contenedor.innerHTML = '';
            return;
        }
        const esc = escapeHtmlParaInyeccionSegura;
        const red = vista.red || {};
        const TIPO_NODO_LABEL = {
            operador: t('Operador'),
            representante: t('Representante'),
            punto_venta: t('Punto de Venta'),
        };
        const tipoLabel = TIPO_NODO_LABEL[red.tipo] || red.tipo || '';
        const redViva = vista.redViva || { cajasOnline: 0, cajasTotal: 0 };
        contenedor.innerHTML = `
            <div class="kpi-card" style="margin-bottom:8px;display:flex;align-items:center;gap:12px;flex-wrap:wrap">
                <div>
                    <div class="kpi-label">${t('Mi red')}</div>
                    <div class="kpi-value" style="font-size:20px">${esc(red.nombre || '')}
                        <span style="font-size:12px;color:var(--text-secondary)">· ${esc(tipoLabel)}</span>
                    </div>
                </div>
                <div style="margin-left:auto;text-align:right">
                    <div class="kpi-label">${icon('monitor')} ${redViva.cajasOnline}/${redViva.cajasTotal} ${t('cajas online')}</div>
                </div>
            </div>
            <div class="kpi-label" style="margin:4px 0 6px;text-transform:uppercase;letter-spacing:0.5px">${t('Acción necesaria')}</div>
            <div class="kpi-grid kpi-grid--3col">${this._renderTarjetasAccionNecesaria(vista.accionRequerida || {})}</div>
            <div class="kpi-grid kpi-grid--3col" style="margin-top:8px">
                ${this._renderTarjetaSaldoOperativoDeMiRed(vista.saldoOperativo)}
                ${this._renderTarjetaTendencia7d(vista.tendencia7d || [])}
            </div>`;
        contenedor.style.display = '';
    },

    // Navegación de las tarjetas "Acción necesaria" — mismo patrón que
    // handleAlertaClick: navigate(vista) + click diferido al tab destino.
    ccOperativoNavegar(destino) {
        const DESTINOS = {
            'finanzas-cierres': { vista: 'finanzas', tab: 'cierres' },
            'actividad-tickets': { vista: 'actividad', tab: 'tickets' },
            'plataforma-dispositivos': { vista: 'plataforma', tab: 'dispositivos' },
        };
        const destinoResuelto = DESTINOS[destino];
        if (!destinoResuelto) return;
        this.navigate(destinoResuelto.vista);
        setTimeout(() => {
            document.querySelector(`#view-${destinoResuelto.vista} [data-tab="${destinoResuelto.tab}"]`)?.click();
        }, 100);
    },

    // Popular dropdown de configs de LotoWheel
    populateConfigDropdown() {
        const select = document.getElementById('sorteoConfigSelect');
        if (!select) return;

        const currentValue = this.sorteoConfigFilter;
        select.innerHTML =
            '<option value="all">' +
            t('Todos los juegos') +
            '</option>' +
            this.sorteoConfigs.map(c => `<option value="${c.id}">${c.nombre}</option>`).join('');
        select.value = currentValue;
    },

    // Filtrar sorteos por búsqueda y chips
    filterSorteos() {
        const searchQuery = document.getElementById('sorteoSearchInput')?.value?.trim() || '';
        let results = this.currentSorteos;

        // Aplicar búsqueda fuzzy si hay query
        if (searchQuery.length > 0 && this.sorteoSearcher) {
            const fuzzyResults = this.sorteoSearcher.search(searchQuery);
            results = fuzzyResults.map(r => this.currentSorteos.find(s => s.id === r.item.id)).filter(Boolean);
        }

        // Aplicar filtros de chips. En filas scoped (miRed, no-superadmin) el
        // criterio equivalente es la actividad/margen de SU red en tickets —
        // el margen global del sorteo no viaja en la respuesta.
        if (this.sorteoFilters.conApuestas) {
            results = results.filter(s => (s.miRed ? s.miRed.tickets : s.apuestasCount) > 0);
        }
        if (this.sorteoFilters.casaGana) {
            results = results.filter(s => this._margenVisibleDeSorteo(s) > 0);
        }
        if (this.sorteoFilters.casaPierde) {
            results = results.filter(s => this._margenVisibleDeSorteo(s) < 0);
        }

        this.filteredSorteos = results;

        // Actualizar contador
        const countEl = document.getElementById('sorteoFilterCount');
        if (countEl) {
            const total = this.currentSorteos.length;
            const shown = results.length;
            countEl.textContent = shown < total ? `${shown}/${total}` : '';
        }

        this.renderSorteosList();
    },

    // Margen que el usuario tiene derecho a ver: el global del sorteo para
    // superadmin, o vendido−premiadoCobrado de SU red (tickets) en filas scoped.
    // premiadoCobrado (no premiado/devengado) para que este resumen cuadre con
    // fechamento/corte — el devengado se muestra aparte en la fila (ver _renderFilaSorteoMiRed).
    _margenVisibleDeSorteo(s) {
        if (!s.miRed) return s.margen;
        // Sin consolidar (monedas mixtas sin tasa): null − null daría 0 y la fila
        // pasaría por "sin margen" — se devuelve null y los filtros la excluyen.
        if (s.miRed.vendido === null || s.miRed.premiadoCobrado === null) return null;
        return s.miRed.vendido - s.miRed.premiadoCobrado;
    },

    // Toggle filtro de chip
    toggleSorteoFilter(filterName) {
        // Casa+ y Casa- son mutuamente excluyentes
        if (filterName === 'casaGana' && !this.sorteoFilters.casaGana) {
            this.sorteoFilters.casaPierde = false;
            document.querySelector('.chip-filter[data-filter="casaPierde"]')?.classList.remove('active');
        } else if (filterName === 'casaPierde' && !this.sorteoFilters.casaPierde) {
            this.sorteoFilters.casaGana = false;
            document.querySelector('.chip-filter[data-filter="casaGana"]')?.classList.remove('active');
        }

        this.sorteoFilters[filterName] = !this.sorteoFilters[filterName];

        // Actualizar UI del chip
        const chip = document.querySelector(`.chip-filter[data-filter="${filterName}"]`);
        if (chip) {
            chip.classList.toggle('active', this.sorteoFilters[filterName]);
        }

        this.filterSorteos();
    },

    // Cambiar rango de sorteos (dropdown)
    changeSorteoRange() {
        const select = document.getElementById('sorteoRangeSelect');
        if (select) {
            this.sorteoRange = select.value;
            this.loadCommandCenter();
        }
    },

    // Cambiar config de LotoWheel (dropdown)
    changeSorteoConfig() {
        const select = document.getElementById('sorteoConfigSelect');
        if (select) {
            this.sorteoConfigFilter = select.value;
            this.loadCommandCenter();
        }
    },

    /**
     * Color del número ganador de un sorteo según la receta de su tipo de
     * rueda. Devuelve un string en castellano que matchea las clases CSS del
     * admin (`'verde'|'rojo'|'negro'`). Agnóstico al tipo: consulta la factory
     * del `tipoRuleta` del sorteo — no asume european.
     *
     * Orden de resolución (importa: cada paso es un fallback honesto al
     * siguiente, sin enmascarar bugs):
     *   1. `numero === 0` (entero estricto) → siempre `'verde'`. En LOTO el
     *      cero es siempre verde por contrato del juego; cualquier receta
     *      LOTO declara el sector 0 con `esCero:true`. Resolverlo aquí
     *      preserva el color correcto para sorteos pre-v56 sin `tipoRuleta`
     *      (el cero seguirá siendo verde como antes del refactor — sin
     *      enmascarar nada). El check es estricto en tipo + valor: si por
     *      algún error de upstream llegara `"0"` string, NO se considera
     *      cero — pasa al fallback de tipoRuleta para no inventar verde.
     *   2. `!tipoRuleta` → `'negro'`. Sorteo pre-v56 sin snapshot del tipo;
     *      no podemos consultar receta. `'negro'` es la opción honesta:
     *      indica al operador que el color no fue resuelto desde la receta
     *      del sorteo (en vez de inventar uno european).
     *   3. Cualquier excepción de la factory (tipo desconocido, paleta
     *      inexistente) → `'negro'` + `console.error` con el contexto. El
     *      log NO enmascara: deja un rastro explícito en la consola del admin
     *      con `tipoRuleta`, `numero` y el mensaje original del error, para
     *      que el operador pueda reportar y la causa raíz no quede silenciada.
     *
     * @param {number} numero - sector ganador del sorteo (entero esperado).
     * @param {string|null} tipoRuleta - id del tipo de rueda del sorteo.
     * @returns {'verde'|'rojo'|'negro'}
     */
    /**
     * Paleta agnostica del numero ganador del sorteo. Devuelve 'paleta-N' o ''
     * (sin paleta resoluble — tema o tipoRuleta no disponibles). El CSS pinta
     * la bolita via [data-paleta] usando las CSS vars del tema activo.
     */
    getNumeroPaleta(numero, tipoRuleta) {
        if (!tipoRuleta) return PALETA_VACIA;
        try {
            return crearConfiguracionDeRuedaParaTipo(tipoRuleta).obtenerClaveDePaletaDeSector(numero) || PALETA_VACIA;
        } catch (err) {
            // Log explicito: la causa raiz queda visible (CLAUDE.md §2A + §1.8).
            console.error('[getNumeroPaleta] tipoRuleta desconocido o receta corrupta', { tipoRuleta, numero, err });
            return PALETA_VACIA;
        }
    },

    getRtpClass(rtp) {
        if (rtp < 60) return 'low';
        if (rtp > 120) return 'high';
        return 'normal';
    },

    renderSorteosList() {
        const list = document.getElementById('sorteosList');
        const hasFilters =
            Object.values(this.sorteoFilters).some(Boolean) ||
            document.getElementById('sorteoSearchInput')?.value?.trim();

        // Si hay filtros activos, usar filteredSorteos (aunque sea vacío)
        const sorteos = hasFilters ? this.filteredSorteos : this.currentSorteos;

        if (sorteos.length === 0) {
            list.innerHTML = `<div style="text-align:center;padding:40px;color:var(--text-secondary)">${hasFilters ? t('Sin resultados para los filtros aplicados') : t('No hay sorteos hoy')}</div>`;
            return;
        }

        // Filas scoped (no-superadmin): traen miRed (actividad en tickets de SU
        // red) y NO traen dinero global → columnas y header distintos.
        const esVistaScopedMiRed = !!sorteos[0]?.miRed;

        // Header de la tabla
        const header = `
        <div class="sorteo-row sorteo-header" style="background:transparent;border:none;cursor:default;font-size:11px;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:0.5px;padding:8px 16px;">
          <div class="sorteo-num">${t('Sorteo')}</div>
          <div class="sorteo-numero">Número</div>
          ${
              esVistaScopedMiRed
                  ? `<div class="sorteo-money">${t('Actividad de mi red (tickets)')}</div>
          <div class="sorteo-margen">${t('Tickets')}</div>
          <div class="sorteo-rtp"></div>`
                  : `<div class="sorteo-money" title="${escapeHtmlParaInyeccionSegura(t('"Premios" es lo que ganó el jugador al momento del sorteo, aunque todavía no lo haya cobrado en caja. Puede no coincidir con el cierre del día.'))}">Apostado → Pagado</div>
          <div class="sorteo-margen" title="${escapeHtmlParaInyeccionSegura(t('"Premios" es lo que ganó el jugador al momento del sorteo, aunque todavía no lo haya cobrado en caja. Puede no coincidir con el cierre del día.'))}">Margen</div>
          <div class="sorteo-rtp">RTP</div>`
          }
          <div class="sorteo-expand"></div>
        </div>`;

        list.innerHTML =
            header +
            sorteos
                .map(s => {
                    const paleta = this.getNumeroPaleta(s.numero, s.tipoRuleta);
                    const isExpanded = this.expandedSorteoId === s.id;
                    if (s.miRed) return this._renderFilaSorteoMiRed(s, paleta, isExpanded);
                    return `
        <div class="sorteo-row ${isExpanded ? 'expanded' : ''}" data-action="app:toggleSorteo" data-id="${s.id}">
          <div class="sorteo-num">#${s.numSorteo}</div>
          <div class="sorteo-numero">
            <div class="sorteo-numero-ball" data-paleta="${paleta}">${s.numero}</div>
          </div>
          <div class="sorteo-money">
            <span class="in">${this.formatMoney(s.totalApostado)}</span>
            <span class="arrow">→</span>
            <span class="out">${this.formatMoney(s.totalPagado)}</span>
          </div>
          <div class="sorteo-margen ${s.margen >= 0 ? 'positive' : 'negative'}">
            ${s.margen >= 0 ? '+' : ''}${this.formatMoney(s.margen)}
          </div>
          <div class="sorteo-rtp ${this.getRtpClass(s.retornoPct)}">${s.retornoPct}${t('% RTP')}</div>
          <div class="sorteo-expand">${icon('chevron-down')}</div>
        </div>
        <div class="sorteo-detail ${isExpanded ? 'open' : ''}" id="sorteo-detail-${s.id}">
          <div class="sorteo-detail-stats">
            <div>${t('Apuestas: ')}<span>${s.apuestasCount}</span></div>
            <div>${t('Ganadores: ')}<span>${s.ganadoresCount}</span></div>
          </div>
          ${
              s.apuestasCount > 0
                  ? `<div class="sorteo-ver-lista" data-action="app:openSorteoModal" data-id="${s.id}">
            ${icon('list')} ${t('Ver lista completa')}
          </div>`
                  : `<div style="color:var(--text-tertiary)">${t('Sin apuestas')}</div>`
          }
        </div>
      `;
                })
                .join('');
        // Iconos ya son <img> locales, no requiere inicializacion
    },

    /**
     * Fila de sorteo para usuario scoped (no-superadmin): muestra SOLO la
     * actividad de SU red en tickets (vendido→premiado, tickets) — sin
     * margen/RTP global ni modal de apuestas (esos datos no viajan).
     */
    _renderFilaSorteoMiRed(s, paleta, isExpanded) {
        const miRed = s.miRed;
        const sinActividad = miRed.tickets === 0 && miRed.vendido === 0;
        const hayPendienteDeCobro = !sinActividad && miRed.premiado !== miRed.premiadoCobrado;
        // Dinero null = monedas mixtas sin tasa (hueco honesto, avisado por el
        // title); consolidado con tasas = prefijo ≈ (la tasa es del día más reciente).
        const metaMiRed = this.miRedMonedaMeta || {};
        const dineroMiRed = v =>
            v === null
                ? `<span title="${t('Sin consolidar (varias monedas)')}">—</span>`
                : (metaMiRed.consolidado_aproximado ? '≈ ' : '') + this.formatMoney(v);
        return `
        <div class="sorteo-row ${isExpanded ? 'expanded' : ''}" data-action="app:toggleSorteo" data-id="${s.id}">
          <div class="sorteo-num">#${s.numSorteo}</div>
          <div class="sorteo-numero">
            <div class="sorteo-numero-ball" data-paleta="${paleta}">${s.numero}</div>
          </div>
          <div class="sorteo-money">
            ${
                sinActividad
                    ? `<span style="color:var(--text-tertiary)">${t('Sin actividad')}</span>`
                    : `<span class="in">${dineroMiRed(miRed.vendido)}</span>
            <span class="arrow">&#8594;</span>
            <span class="out" title="${t('Cobrado en caja')}">${dineroMiRed(miRed.premiadoCobrado)}</span>${
                hayPendienteDeCobro
                    ? `<span class="out" style="opacity:.65; font-size:11px; margin-left:4px" title="${t('Ganado, pendiente de cobro')}">(${t('Ganado')} ${dineroMiRed(miRed.premiado)})</span>`
                    : ''
            }`
            }
          </div>
          <div class="sorteo-margen">${sinActividad ? '' : miRed.tickets}</div>
          <div class="sorteo-rtp"></div>
          <div class="sorteo-expand">${icon('chevron-down')}</div>
        </div>
        <div class="sorteo-detail ${isExpanded ? 'open' : ''}" id="sorteo-detail-${s.id}">
          ${
              sinActividad
                  ? `<div style="color:var(--text-tertiary)">${t('Sin actividad')}</div>`
                  : `<div class="sorteo-detail-stats">
            <div>${t('Tickets')}: <span>${miRed.tickets}</span></div>
            <div>${t('Ganadores: ')}<span>${miRed.ganadores}</span></div>
          </div>`
          }
        </div>
      `;
    },

    toggleSorteo(id) {
        this.expandedSorteoId = this.expandedSorteoId === id ? null : id;
        this.renderSorteosList();
    },

    async openSorteoModal(id) {
        const sorteo = this.currentSorteos.find(s => s.id === id);
        if (!sorteo) return;

        this.openModal('modalSorteo');
        document.getElementById('modalSorteoTitle').textContent = t('Sorteo #') + sorteo.numSorteo;

        const paleta = this.getNumeroPaleta(sorteo.numero, sorteo.tipoRuleta);
        const ballEl = document.getElementById('sorteoNumeroGrande');
        ballEl.textContent = sorteo.numero;
        ballEl.className = 'sorteo-numero-grande';
        ballEl.setAttribute('data-paleta', paleta);
        // El estilo de la bolita viene del CSS via [data-paleta] usando vars del
        // tema. Sin inline-style hardcoded por color.
        ballEl.style.background = '';

        document.getElementById('sorteoApostado').textContent = this.formatMoney(sorteo.totalApostado);
        document.getElementById('sorteoPagado').textContent = this.formatMoney(sorteo.totalPagado);
        document.getElementById('sorteoRTP').textContent = sorteo.retornoPct + '%';

        this.currentSorteoApuestas = sorteo.apuestas || [];
        this.renderSorteoApuestas();

        const searchInput = document.getElementById('sorteoSearch');
        searchInput.value = '';
        searchInput.oninput = () => this.filterSorteoApuestas(searchInput.value);
    },

    renderSorteoApuestas(filter = '') {
        const list = document.getElementById('sorteoApuestasList');
        let apuestas = this.currentSorteoApuestas;

        if (filter) {
            const f = filter.toLowerCase();
            apuestas = apuestas.filter(
                a =>
                    (a.tel && a.tel.includes(f)) ||
                    (a.tipo && a.tipo.toLowerCase().includes(f)) ||
                    (a.puntoVenta && String(a.puntoVenta).includes(f))
            );
        }

        if (apuestas.length === 0) {
            list.innerHTML =
                '<div style="text-align:center;padding:20px;color:var(--text-tertiary)">' +
                t('Sin resultados') +
                '</div>';
            return;
        }

        list.innerHTML = apuestas
            .map(
                a => `
      <div class="apuesta-row">
        <div class="apuesta-tel">${a.tel || '-'}</div>
        <div class="apuesta-punto-venta">PV ${a.puntoVenta || '-'}</div>
        <div class="apuesta-tipo">${a.tipo}${a.valor !== null && a.valor !== undefined ? `(${a.valor})` : ''}</div>
        <div class="apuesta-monto">${this.formatMoney(a.monto)}</div>
        <div class="apuesta-ganancia ${a.ganancia > 0 ? 'win' : 'loss'}">${a.ganancia > 0 ? '+' + this.formatMoney(a.ganancia) : '-'}</div>
      </div>
    `
            )
            .join('');
    },

    filterSorteoApuestas(filter) {
        this.renderSorteoApuestas(filter);
    },
};

// Inyectar dependencias del shell en AdminApiClient
// Aquí App ya está definido — sin circularidad en tiempo de importación
AdminApiClient.init({
    getToken: () => App.getToken(),
    onError: msg => App.toast(msg, 'error'),
});

// Init on DOM ready
document.addEventListener('DOMContentLoaded', () => App.init());
